import { useLayoutEffect, useRef, type Ref, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

import { controlBase } from './controlStyles';

type Props = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /**
   * A caller's own ref. Named in the type rather than arriving through `...props`, which is what
   * broke the auto-grow below — see the ⚠️ note.
   */
  ref?: Ref<HTMLTextAreaElement>;
};

/**
 * Every comment field in the product (§4). Auto-growing from a three-row minimum: an interviewer
 * writing an assessment should never be typing into a scrollbar, and a fixed height is what makes
 * people write less than they meant to.
 *
 * ⚠️ **The auto-grow did not work at all until TASK-078 measured it in a real browser (2026-07-29),
 * and the cause was not layout.** `ref={ref}` sat **before** `{...props}`, and `ref` is an ordinary
 * prop in React 19 — so a caller spreading one in replaced the primitive's own. §6.9's edit dialog
 * does exactly that (`<Textarea {...fieldProps} {...form.register('text')} value={values.text} />`;
 * `register()` returns a `ref`), so `element` was always `null`, the effect returned on its first
 * line, and `style.height` was never set. Measured on the built bundle at 1440×900 with a
 * 200-character question: `style.height` **`""`**, box **78 px**, `scrollHeight` **96 px**; after
 * typing to 545 characters, `scrollHeight` **236 px** against a `clientHeight` of **76 px** with
 * `overflow: hidden` — the wording a reader had typed was clipped, with no scrollbar to find it by.
 * That is the exact consequence TASK-074's mutation 24 predicted and could not observe: jsdom
 * computes no layout, so `scrollHeight` is 0 there and the effect's *absence* looked the same as its
 * presence.
 *
 * Two things changed, both additive and defaulted: `ref` is taken out of `...props` so a caller's ref
 * can no longer silently replace this one, and the merged ref is attached **after** the spread so
 * ordering cannot reintroduce it. A caller passing no ref is unaffected; a caller passing one still
 * gets it.
 */
export function Textarea({
  className,
  onChange,
  value,
  rows = 3,
  ref: forwarded,
  ...props
}: Props) {
  const own = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const element = own.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      rows={rows}
      value={value}
      onChange={onChange}
      className={cn(controlBase, 'w-full resize-none overflow-hidden', className)}
      {...props}
      /*
       * After the spread, deliberately (see the note above). The body returns nothing: in React 19 a
       * value returned from a ref callback is treated as a cleanup function.
       */
      ref={(node) => {
        own.current = node;
        if (typeof forwarded === 'function') forwarded(node);
        else if (forwarded) forwarded.current = node;
      }}
    />
  );
}
